import * as React from 'react';
import { classNames, ComponentClassName } from '@aws-amplify/ui';
import { Image } from '@aws-amplify/ui-react';
import { useDeprecationWarning } from '@aws-amplify/ui-react/internal';
import { useSetUserAgent, useGetUrl } from '@aws-amplify/ui-react-core';
import { VERSION } from '../../version.mjs';

const MISSING_REQUIRED_PROP_MESSAGE = '`StorageImage` requires either an `imgKey` or `path` prop.';
const HAS_DEPRECATED_PROPS_MESSAGE = '`imgKey`, `accessLevel`, and `identityId` will be replaced with `path` in a future major version. See https://ui.docs.amplify.aws/react/connected-components/storage/storageimage#props';
const HAS_PATH_AND_KEY_MESSAGE = '`imgKey` is ignored when both `imgKey` and `path` props are provided.';
const HAS_PATH_AND_UNSUPPORTED_OPTIONS_MESSAGE = '`accessLevel` and `identityId` are ignored when the `path` prop is provided.';
const getDeprecationMessage = ({ hasImgkey, hasPath, hasDeprecatedOptions, }) => {
    let message = '';
    if (hasPath && hasImgkey) {
        message = HAS_PATH_AND_KEY_MESSAGE;
    }
    else if (hasPath && hasDeprecatedOptions) {
        message = HAS_PATH_AND_UNSUPPORTED_OPTIONS_MESSAGE;
    }
    else if (hasImgkey) {
        message = HAS_DEPRECATED_PROPS_MESSAGE;
    }
    return message;
};
const StorageImage = ({ accessLevel, className, fallbackSrc, identityId, imgKey, path, onStorageGetError, onGetUrlError, validateObjectExistence = true, ...rest }) => {
    const hasImgkey = !!imgKey;
    const hasPath = !!path;
    const hasDeprecatedOptions = !!accessLevel || !!identityId;
    const message = getDeprecationMessage({
        hasDeprecatedOptions,
        hasImgkey,
        hasPath,
    });
    useDeprecationWarning({ message, shouldWarn: !!message });
    if (!hasImgkey && !hasPath) {
        throw new Error(MISSING_REQUIRED_PROP_MESSAGE);
    }
    useSetUserAgent({
        componentName: 'StorageImage',
        packageName: 'react-storage',
        version: VERSION,
    });
    const onError = onGetUrlError ?? onStorageGetError;
    const input = React.useMemo(() => ({
        ...(path ? { path } : { key: imgKey }),
        onError,
        options: {
            accessLevel,
            targetIdentityId: identityId,
            validateObjectExistence,
        },
    }), [accessLevel, imgKey, identityId, onError, path, validateObjectExistence]);
    const { url } = useGetUrl(input);
    return (React.createElement(Image, { ...rest, className: classNames(ComponentClassName.StorageImage, className), src: url?.toString() ?? fallbackSrc }));
};

export { HAS_DEPRECATED_PROPS_MESSAGE, HAS_PATH_AND_KEY_MESSAGE, HAS_PATH_AND_UNSUPPORTED_OPTIONS_MESSAGE, MISSING_REQUIRED_PROP_MESSAGE, StorageImage };
